import {
  AdMob,
  AdmobConsentDebugGeography,
  AdmobConsentInfo,
  AdmobConsentStatus,
  BannerAdOptions,
  BannerAdPosition,
  BannerAdSize,
} from '@capacitor-community/admob';
import { inject, Injectable, NgZone } from '@angular/core';

const adSize: BannerAdSize = BannerAdSize.BANNER;
const defaultBannerAdId = 'ca-app-pub-6726077912756062/2152336254';
const adPosition: BannerAdPosition = BannerAdPosition.BOTTOM_CENTER;


const testingUids:  string[] = [
  '3qu1O70TFcS70r5ypY4WC0Q6KMy1',
] as const;


@Injectable({
  providedIn: 'root',
})
export class AdService {
  private bannerVisible = false;
  private initialized = false;
  private shouldShowAds = false;

  private _userId: string | null = null;

  // Track if banner was hidden due to modal/overlay
  private hiddenByOverlay = false;
  private wasVisibleBeforeOverlay = false;

  private readonly ngZone = inject(NgZone);

  /**
   * Sets up global listeners for Ionic overlays (modals, action sheets, etc.)
   * to automatically hide/show banner ads
   */
  private setupOverlayListeners(): void {
    document.addEventListener('ionModalWillPresent', () => {
      this.ngZone.run(() => {
        this.handleOverlayPresent();
      });
    });

    document.addEventListener('ionModalDidDismiss', () => {
      this.ngZone.run(() => {
        this.handleOverlayDismiss();
      });
    });

    document.addEventListener('ionActionSheetWillPresent', () => {
      this.ngZone.run(() => {
        this.handleOverlayPresent();
      });
    });

    document.addEventListener('ionActionSheetDidDismiss', () => {
      this.ngZone.run(() => {
        this.handleOverlayDismiss();
      });
    });

    console.log('[ads] overlay listeners configured');
  }

  private handleOverlayPresent(): void {
    console.log('[ads] overlay presented, hiding banner');
    this.wasVisibleBeforeOverlay = this.bannerVisible;
    if (this.bannerVisible) {
      this.hiddenByOverlay = true;
      this.hideBanner();
    }
  }

  private handleOverlayDismiss(): void {
    console.log('[ads] overlay dismissed');
    // Only restore banner if it was visible before the overlay appeared
    if (this.hiddenByOverlay && this.wasVisibleBeforeOverlay) {
      console.log('[ads] restoring banner after overlay dismiss');
      this.hiddenByOverlay = false;
      this.wasVisibleBeforeOverlay = false;
      this.showBanner();
    }
  }

  public async initialize(): Promise<void> {
    if (this.initialized) {
      console.log('[ads] already initialized');
      return;
    }


    console.log('[ads] initializing');

    await AdMob.initialize();

    this.setupOverlayListeners();

    console.log('[ads] initialized');

    console.log('[ads] getting tracking info');

    const trackingInfo = await AdMob.trackingAuthorizationStatus();

    // Request consent info separately with error handling
    // This can fail on iOS due to network issues or WKWebView timing problems
    // Since we're configured for NOT_EEA, consent isn't legally required
    let consentInfo: AdmobConsentInfo | null = null;
    try {
      consentInfo = await AdMob.requestConsentInfo({
        debugGeography: AdmobConsentDebugGeography.NOT_EEA,
        testDeviceIdentifiers: this._isTesting ? testingUids : undefined,
      });
    } catch (error) {
      console.warn('[ads] consent info request failed, continuing without consent:', error);
    }

    console.log('[ads] tracking and consent info', trackingInfo, consentInfo);

    if (trackingInfo.status === 'notDetermined') {
      /**
       * If you want to explain TrackingAuthorization before showing the iOS dialog,
       * you can show the modal here.
       * ex)
       * const modal = await this.modalCtrl.create({
       *   component: RequestTrackingPage,
       * });
       * await modal.present();
       * await modal.onDidDismiss();  // Wait for close modal
       **/

      console.log('[ads] requesting tracking authorization');
      await AdMob.requestTrackingAuthorization();
    }

    const authorizationStatus = await AdMob.trackingAuthorizationStatus();
    console.log('[ads] authorization status', authorizationStatus);
    if (
      authorizationStatus.status === 'authorized' &&
      consentInfo?.isConsentFormAvailable &&
      consentInfo?.status === AdmobConsentStatus.REQUIRED
    ) {
      console.log('[ads] showing consent form');
      await AdMob.showConsentForm();
    }

    console.log('[ads] initialized');
    this.initialized = true;
  }

  public async showBanner(): Promise<void> {
    console.log('[ads] showBanner');

    // Make sure ads are initialized before showing
    if (!this.initialized) {
      throw new Error('Ads not initialized');
    }

    if (!this.shouldShowAds) {
      console.log('[ads] ads not enabled');
      return;
    }

    if (this.bannerVisible) {
      console.log('[ads] banner already visible');
      return;
    }

    await AdMob.showBanner(this.bannerOptions);
    this.bannerVisible = true;
  }

  public async hideBanner(): Promise<void> {
    console.log('[ads] hideBanner');
    if (this.bannerVisible) {
      await AdMob.hideBanner();
      this.bannerVisible = false;
    }
  }

  public isBannerVisible(): boolean {
    return this.bannerVisible;
  }

  public configureAds(options: { enabled: boolean, userId?: string }): void {
    this.shouldShowAds = options.enabled;
    this._userId = options.userId || null;
  }

  private get _isTesting(): boolean {
    if (!this._userId) {
      return false;
    }
    return testingUids.includes(this._userId);
  }

  private get bannerOptions(): BannerAdOptions {
    return {
      adId: defaultBannerAdId,
      adSize: adSize,
      position: adPosition,
      isTesting: this._isTesting,
    };
  }
}
